/**
 * Federal income tax brackets — mirrors TaxReturnUtils.calculateMarginalRate()
 * Source: TaxReturnUtils.java (dated 16 May 2025)
 *
 * Each bracket: [upperBound, rate]
 * upperBound = Infinity means "everything above the previous bracket"
 */

export interface TaxBracket {
  upTo: number;    // income ceiling for this bracket
  rate: number;    // decimal rate (e.g. 0.10 for 10%)
}

export type FilingStatus = 'Single' | 'Married Filing Jointly (MFJ)' | 'Married Filing Separately (MFS)' | 'Head of Household' | 'Estate' | 'Trust';

// ─── 2025 Federal Tax Brackets ──────────────────────────────────────

export const FEDERAL_BRACKETS_2025: { [key: string]: TaxBracket[] } = {
  'Single': [
    { upTo: 12400, rate: 0.10 },
    { upTo: 50400, rate: 0.12 },
    { upTo: 105700, rate: 0.22 },
    { upTo: 201775, rate: 0.24 },
    { upTo: 256225, rate: 0.32 },
    { upTo: 640600, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  'Married Filing Separately (MFS)': [
    { upTo: 12400, rate: 0.10 },
    { upTo: 50400, rate: 0.12 },
    { upTo: 105700, rate: 0.22 },
    { upTo: 201775, rate: 0.24 },
    { upTo: 256225, rate: 0.32 },
    { upTo: 384350, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  'Married Filing Jointly (MFJ)': [
    { upTo: 24800, rate: 0.10 },
    { upTo: 100800, rate: 0.12 },
    { upTo: 211400, rate: 0.22 },
    { upTo: 403550, rate: 0.24 },
    { upTo: 512450, rate: 0.32 },
    { upTo: 768700, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  'Head of Household': [
    { upTo: 17700, rate: 0.10 },
    { upTo: 67450, rate: 0.12 },
    { upTo: 105700, rate: 0.22 },
    { upTo: 201775, rate: 0.24 },
    { upTo: 256200, rate: 0.32 },
    { upTo: 640600, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  'Estate': [
    { upTo: 3300, rate: 0.10 },
    { upTo: 11700, rate: 0.24 },
    { upTo: 16000, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  'Trust': [
    { upTo: 3300, rate: 0.10 },
    { upTo: 11700, rate: 0.24 },
    { upTo: 16000, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
};

/**
 * Get federal brackets for a given year.
 * Currently only 2025 brackets are available from the backend.
 * Future: add 2026+ brackets when IRS publishes them.
 */
export function getFederalBrackets(year: number): { [key: string]: TaxBracket[] } {
  // The backend currently has a single set of brackets (2025).
  // When new year brackets are added to the backend, add them here.
  return FEDERAL_BRACKETS_2025;
}

/**
 * Compute the dollar amount of federal tax using progressive brackets.
 * This is different from the backend's calculateMarginalRate() which returns
 * only the marginal rate — we need the actual tax dollars.
 */
export function computeFederalTax(taxableIncome: number, filingStatus: string): number {
  const brackets = getFederalBrackets(new Date().getFullYear())[filingStatus];
  if (!brackets || taxableIncome <= 0) return 0;

  let tax = 0;
  let previousUpTo = 0;

  for (const bracket of brackets) {
    const taxableInBracket = Math.min(taxableIncome, bracket.upTo) - previousUpTo;
    if (taxableInBracket <= 0) break;
    tax += taxableInBracket * bracket.rate;
    previousUpTo = bracket.upTo;
  }

  return Math.round(tax * 100) / 100;
}

/**
 * Get the marginal rate for a given taxable income (matches backend behavior).
 */
export function getMarginalRate(taxableIncome: number, filingStatus: string): number {
  const brackets = getFederalBrackets(new Date().getFullYear())[filingStatus];
  if (!brackets || taxableIncome <= 0) return 0;

  let rate = 0;
  for (const bracket of brackets) {
    rate = bracket.rate;
    if (taxableIncome <= bracket.upTo) break;
  }
  return rate * 100; // return as percentage (e.g. 24 not 0.24)
}

/**
 * C-Corp flat federal rate
 */
export const C_CORP_FLAT_RATE = 21;

/**
 * Self-employment tax rate
 */
export const SE_TAX_RATE = 15.3;
